import React from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { HookEndpointResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

const validationSchema = Yup.object().shape({
  key: Yup.string()
    .matches(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, numbers, and underscores only. Must start with a letter.')
    .required('Key is required'),
  name: Yup.string().required('Name is required'),
  callback_url: Yup.string().url('Must be a valid URL').required('Callback URL is required'),
  timeout_seconds: Yup.number()
    .min(1, 'Minimum 1 second')
    .max(60, 'Maximum 60 seconds')
    .required('Timeout is required'),
});

interface FormValues {
  key: string;
  name: string;
  callback_url: string;
  timeout_seconds: number;
  keyManuallyEdited: boolean;
}

interface HookEndpointDialogProps {
  organizationId: number;
  existingEndpoint: HookEndpointResponse | null;
  onClose: () => void;
}

const nameToKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const HookEndpointDialogInner: React.FC<{ onClose: () => void; isNew: boolean }> = ({ onClose, isNew }) => {
  const { handleSubmit, isSubmitting, values, setFieldValue, setFieldTouched } = useFormikContext<FormValues>();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFieldValue('name', newName);
    if (isNew && !values.keyManuallyEdited) {
      setFieldValue('key', nameToKey(newName));
      setFieldTouched('key', false);
    }
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('key', e.target.value);
    setFieldValue('keyManuallyEdited', true);
  };

  return (
    <CardDialog
      title={isNew ? 'Create Hook Endpoint' : 'Edit Hook Endpoint'}
      isDialog
      open={true}
      maxWidth="sm"
      onClose={onClose}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSubmit}
          onClickSecondary={onClose}
          primaryLabel="Save"
          showPrimary
          secondaryLabel="Cancel"
          disabled={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-20">
        <TextFieldFormik
          label="Name"
          id="name"
          className="w-full"
          // @ts-expect-error - TextFieldFormik accepts onChange at runtime
          onChange={handleNameChange}
        />
        <TextFieldFormik
          label="Key"
          id="key"
          className="w-full"
          disabled={!isNew}
          // @ts-expect-error - TextFieldFormik accepts onChange at runtime
          onChange={isNew ? handleKeyChange : undefined}
        />
        <TextFieldFormik
          label="Callback URL"
          name="callback_url"
          placeholder="https://example.com/hooks"
          id="callback_url"
          className="w-full"
        />
        <TextFieldFormik label="Timeout (seconds)" name="timeout_seconds" id="timeout_seconds" className="w-full" />
      </div>
    </CardDialog>
  );
};

const HookEndpointDialog: React.FC<HookEndpointDialogProps> = ({ organizationId, existingEndpoint, onClose }) => {
  const isNew = !existingEndpoint;
  const baseUrl = `/sysconfig/api/v1/organizations/${organizationId}/hook_endpoints`;

  const handleSubmit = async (values: FormValues) => {
    try {
      if (isNew) {
        await axios.post(baseUrl, {
          key: values.key,
          name: values.name,
          callback_url: values.callback_url,
          timeout_seconds: values.timeout_seconds,
        });
      } else {
        await axios.patch(`${baseUrl}/${existingEndpoint.key}`, {
          name: values.name,
          callback_url: values.callback_url,
          timeout_seconds: values.timeout_seconds,
        });
      }
      onClose();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const initialValues: FormValues = {
    name: existingEndpoint?.name ?? '',
    key: existingEndpoint?.key ?? '',
    callback_url: existingEndpoint?.callback_url ?? '',
    timeout_seconds: existingEndpoint?.timeout_seconds ?? 10,
    keyManuallyEdited: !!existingEndpoint,
  };

  return (
    <Formik<FormValues> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      <HookEndpointDialogInner onClose={onClose} isNew={isNew} />
    </Formik>
  );
};

export default HookEndpointDialog;
